import React, { FormEvent, useCallback, useState } from 'react';
import { Button, Input, Modal } from '@cognitiv/cyprus-ui';
import { useAppDispatch, useAppSelector } from 'ducks/hooks';
import { selectCreateProject } from 'ducks/modals/selectors';
import { setModal } from 'ducks/modals/slices';
import { createProject } from 'ducks/projects/operators';
import { ERROR_LEVEL, ProjectFormState } from 'ducks/types/common-types';
import { useQuerySave } from 'hooks/useQuerySave';
import { useSwitchTheme } from 'hooks/useSwitchTheme';
import { useValidate } from 'hooks/useValidation';
import { default_state, validation } from 'modals/project/constants';
import { shallowEqual } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { getValidationError, handleError } from 'utils/errors';

import cn from 'modals/Modal.module.scss';

export const CreateProject = () => {
  const dispatch = useAppDispatch();
  const { search } = useLocation();
  const navigate = useNavigate();
  const { theme } = useSwitchTheme();

  const create_project = useAppSelector(selectCreateProject, shallowEqual);

  const { getJobsQuery } = useQuerySave({ page: null, search });
  const [form, setForm] = useState<ProjectFormState>({ ...default_state });
  const { errors, validate, resetFieldError } = useValidate(form, validation);

  const onChange = useCallback(
    (item: Partial<ProjectFormState>) => {
      resetFieldError(item);
      setForm((form) => ({ ...form, ...item }));
    },
    [resetFieldError],
  );

  const onClose = useCallback(() => {
    dispatch(setModal({ create_project: false }));
  }, [dispatch]);

  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const is_valid = validate();

    if (!is_valid) return;

    try {
      const res: any = await createProject(form);
      if (res?.error) return;

      navigate(`/projects/${res}/jobs/all${getJobsQuery()}`);
    } catch (err) {
      handleError(err);
    }
    onClose();
  };

  return (
    <Modal
      name="create_project"
      show={create_project}
      width={450}
      padding={0}
      close_box={50}
      onClose={onClose}
      standard={false}
    >
      <h2
        className={cn.header}
        style={{
          backgroundColor: theme.modal.modal_background,
          color: theme.font_color,
          borderBottom: theme.border,
        }}
      >
        Create Project
      </h2>
      <form className={cn.body} onSubmit={onSubmit}>
        <Input
          auto_focus
          label="Project Name"
          value={form.project_name}
          margin="0px 0px 0px 0px"
          onChange={(item: string) => onChange({ project_name: item })}
          error_message={errors.find((error) => error.key === 'project_name')?.error}
          error_level={
            getValidationError<ProjectFormState>({ errors, key: 'project_name' }) ? ERROR_LEVEL.ERROR : undefined
          }
        />
        <div className={cn.buttonContainer}>
          <Button type="submit" width={115} button_size="medium" margin="10px 0px 0px 0px">
            Create
          </Button>
        </div>
      </form>
    </Modal>
  );
};
